import { ofType } from 'redux-observable'
import { EMPTY, interval, of } from 'rxjs'
import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators'

import { combineEpicsIsolated } from 'utils/epicRegistry'

import { selectActualFeatureFlags, selectIsStaticSalesDemo } from 'containers/App/selectors'

import {
  DASHBOARD_MOUNTED,
  DASHBOARD_UNMOUNTED,
  GET_ADOPTION_ASYNC,
  GET_BRIEFING_ASYNC,
  GET_DASHBOARD_ASYNC,
  GET_DMARC_ASYNC,
  GET_FEED_ASYNC,
  GET_PERFORMANCE_ASYNC,
  GET_REDIRECTS_ASYNC,
  GET_TRENDS_OVERVIEW_ASYNC,
  REFRESH_ALL,
  SET_DAYS_BACK,
  SET_PAGE_VISIBLE,
} from './constants'
import { selectDaysBack, selectPageVisible } from './selectors'

const FEED_POLL_INTERVAL_MS = 30_000
const FEED_PAGE_SIZE = 20

const isFeatureOn = (state) => {
  const ff = selectActualFeatureFlags(state)
  return !selectIsStaticSalesDemo(state) && Boolean(ff?.ENABLE_THEMIS_AI_ANALYSIS_DASHBOARD)
}

// Auth + PSA token are handled by the BE proxy. The FE just sends session-authed GETs.
const makeAnalyticsEpic =
  (asyncType, apiCall) =>
  (action$, state$, { api, epicsErrorHandler }) =>
    action$.pipe(
      ofType(asyncType.INITIATE),
      filter(() => isFeatureOn(state$.value)),
      mergeMap(() => {
        const daysBack = selectDaysBack(state$.value)
        return apiCall(api, daysBack).pipe(
          map((response) => asyncType.onSuccess(response)),
          catchError((error) => of(epicsErrorHandler(asyncType.onError, error))),
          takeUntil(action$.pipe(ofType(asyncType.CANCEL)))
        )
      })
    )

const getDashboardEpic = makeAnalyticsEpic(GET_DASHBOARD_ASYNC, (api, d) =>
  api.agentsDashboard.getDashboard(d)
)
const getPerformanceEpic = makeAnalyticsEpic(GET_PERFORMANCE_ASYNC, (api, d) =>
  api.agentsDashboard.getPerformance(d)
)
const getDmarcEpic = makeAnalyticsEpic(GET_DMARC_ASYNC, (api, d) => api.agentsDashboard.getDmarc(d))
const getRedirectsEpic = makeAnalyticsEpic(GET_REDIRECTS_ASYNC, (api, d) =>
  api.agentsDashboard.getRedirects(d)
)
const getAdoptionEpic = makeAnalyticsEpic(GET_ADOPTION_ASYNC, (api, d) =>
  api.agentsDashboard.getAdoption(d)
)
const getTrendsOverviewEpic = makeAnalyticsEpic(GET_TRENDS_OVERVIEW_ASYNC, (api, d) =>
  api.agentsDashboard.getTrendsOverview(d)
)
const getBriefingEpic = makeAnalyticsEpic(GET_BRIEFING_ASYNC, (api, d) =>
  api.agentsDashboard.getBriefing(d)
)

// One trigger → 7 analytics initiates + 1 feed initiate.
const refreshAllEpic = (action$, state$) =>
  action$.pipe(
    ofType(REFRESH_ALL, DASHBOARD_MOUNTED, SET_DAYS_BACK),
    filter(() => isFeatureOn(state$.value)),
    mergeMap(() =>
      of(
        GET_DASHBOARD_ASYNC.initiate(),
        GET_PERFORMANCE_ASYNC.initiate(),
        GET_DMARC_ASYNC.initiate(),
        GET_REDIRECTS_ASYNC.initiate(),
        GET_ADOPTION_ASYNC.initiate(),
        GET_TRENDS_OVERVIEW_ASYNC.initiate(),
        GET_BRIEFING_ASYNC.initiate(),
        GET_FEED_ASYNC.initiate()
      )
    )
  )

// Live feed poll, every 30s, paused while the page is hidden.
const feedPollEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DASHBOARD_MOUNTED, DASHBOARD_UNMOUNTED, SET_PAGE_VISIBLE),
    switchMap((action) => {
      if (action.type === DASHBOARD_UNMOUNTED) return EMPTY
      if (action.type === SET_PAGE_VISIBLE && action.payload === false) {
        return EMPTY
      }
      if (!isFeatureOn(state$.value)) return EMPTY

      const tick$ = interval(FEED_POLL_INTERVAL_MS).pipe(startWith(0))

      return tick$.pipe(
        filter(() => selectPageVisible(state$.value)),
        exhaustMap(() => {
          const daysBack = selectDaysBack(state$.value)
          return api.agentsDashboard.getInvestigationsFeed(FEED_PAGE_SIZE, null, daysBack).pipe(
            map((response) => GET_FEED_ASYNC.onSuccess(response)),
            catchError((error) => of(epicsErrorHandler(GET_FEED_ASYNC.onError, error)))
          )
        }),
        takeUntil(action$.pipe(ofType(DASHBOARD_UNMOUNTED)))
      )
    })
  )

// GET_FEED_ASYNC.INITIATE → one-shot fetch (used by REFRESH_ALL fan-out / manual refresh).
const feedInitiateEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_FEED_ASYNC.INITIATE),
    filter(() => isFeatureOn(state$.value)),
    mergeMap(() => {
      const daysBack = selectDaysBack(state$.value)
      return api.agentsDashboard.getInvestigationsFeed(FEED_PAGE_SIZE, null, daysBack).pipe(
        map((response) => GET_FEED_ASYNC.onSuccess(response)),
        catchError((error) => of(epicsErrorHandler(GET_FEED_ASYNC.onError, error))),
        takeUntil(action$.pipe(ofType(GET_FEED_ASYNC.CANCEL)))
      )
    })
  )

export default combineEpicsIsolated(
  refreshAllEpic,
  getDashboardEpic,
  getPerformanceEpic,
  getDmarcEpic,
  getRedirectsEpic,
  getAdoptionEpic,
  getTrendsOverviewEpic,
  getBriefingEpic,
  feedInitiateEpic,
  feedPollEpic
)
